/**
 * PROMOS — one page for the promo engine. One set of promos, one status
 * system, one tick. A promo can be used as a screen now and as a button in
 * the Review Shield flow later, from the same record.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Megaphone, Plus, Trash2, Loader2, ExternalLink } from 'lucide-react';

import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { PromoEditor } from '../components/promos/PromoEditor';

const STATUS_TONE = {
    on: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    draft: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
    off: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
};

export default function Promos() {
    const { t } = useTranslation();
    const [promos, setPromos] = useState(null);
    const [orgs, setOrgs] = useState([]);
    const [org, setOrg] = useState('');
    const [editing, setEditing] = useState(null);

    useEffect(() => {
        api.get('/organizations')
            .then((r) => {
                const list = r.data.organizations || r.data || [];
                setOrgs(list);
                setOrg((prev) => prev || list[0]?.id || '');
            })
            .catch(() => setOrgs([]));
    }, []);

    const load = useMemo(() => async (orgId) => {
        if (!orgId) return;
        try {
            const r = await api.get('/promos', { params: { organization_id: orgId } });
            setPromos(r.data.promos || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('promo.loadFailed', 'Could not load promos'));
            setPromos([]);
        }
    }, [t]);

    useEffect(() => { load(org); }, [org, load]);

    const remove = async (promo) => {
        if (!window.confirm(t('promo.confirmDelete', 'Delete this promo?'))) return;
        try {
            await api.delete(`/promos/${promo.id}`);
            toast.success(t('promo.deleted', 'Promo deleted'));
            load(org);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('promo.saveFailed', 'Could not save the promo'));
        }
    };

    return (
        <div className="space-y-6" data-testid="promos-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-primary" />
                    <h1 className="text-2xl font-bold">{t('promo.title', 'Promos')}</h1>
                </div>
                <div className="flex items-center gap-2">
                    <select value={org} onChange={(e) => setOrg(e.target.value)}
                            data-testid="promos-org-select"
                            className="h-9 rounded-md border bg-background px-3 text-sm">
                        {orgs.map((o) => (
                            <option key={o.id} value={o.id}>{o.display_name || o.name}</option>
                        ))}
                    </select>
                    <Button onClick={() => setEditing({})} data-testid="promo-new-btn" disabled={!org}>
                        <Plus className="w-4 h-4 mr-1" /> {t('promo.new', 'New promo')}
                    </Button>
                </div>
            </div>

            {promos === null ? (
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            ) : promos.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="promos-empty">
                    {t('promo.empty', 'No promos yet.')}
                </p>
            ) : (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {promos.map((p) => (
                        <Card key={p.id} data-testid={`promo-card-${p.id}`}>
                            <CardContent className="p-4 space-y-3">
                                <div className="flex items-start justify-between gap-2">
                                    <p className="font-semibold">{p.name || '—'}</p>
                                    <Badge className={STATUS_TONE[p.status] || STATUS_TONE.draft}
                                           data-testid={`promo-status-${p.id}`}>
                                        {t(`promo.status.${p.status}`, p.status)}
                                    </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground break-all">
                                    {p.link || t('promo.noLink', 'No link yet')}
                                </p>
                                <div className="flex items-center gap-2">
                                    <span className="h-6 w-10 rounded-md border"
                                          style={{ background: p.bg_color }} />
                                    <span className="text-xs text-muted-foreground">
                                        {p.target === 'selection'
                                            ? t('promo.targetSome', '{{n}} cards selected', { n: (p.card_ids || []).length })
                                            : t('promo.targetAll', 'All cards of the organization')}
                                    </span>
                                </div>
                                <div className="flex gap-2 pt-1">
                                    <Button size="sm" variant="outline" onClick={() => setEditing(p)}
                                            data-testid={`promo-edit-${p.id}`}>
                                        {t('promo.edit', 'Edit')}
                                    </Button>
                                    {p.link && (
                                        <Button size="sm" variant="ghost" asChild>
                                            <a href={p.link} target="_blank" rel="noopener noreferrer">
                                                <ExternalLink className="w-3.5 h-3.5" />
                                            </a>
                                        </Button>
                                    )}
                                    <Button size="sm" variant="ghost" onClick={() => remove(p)}
                                            data-testid={`promo-delete-${p.id}`}>
                                        <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {editing && (
                <PromoEditor promo={editing} orgId={org}
                             onClose={() => setEditing(null)}
                             onSaved={() => { setEditing(null); load(org); }} />
            )}
        </div>
    );
}
